import posed, { PoseGroup } from 'react-pose';
import * as React from 'react';
import { Text } from '@gje/typography/lib';
import { Coverage } from '../../../types/Coverage';
import { HtmlPanel } from '../HtmlPanel/HtmlPanel';
import classNames from 'classnames';
import useScroll from '../../../hooks/useScroll';
import useNextRenderCall from '../../../hooks/useNextRenderCall';
import { OpacityValues } from '../../../styles/enums/CommonStyleValues';
import { Check } from '@gje/icons';
import { isOkq8 } from '../../../services/partners/partnersService';

interface CoverageProps {
    coverageData: Coverage;
    availableCoverages: string[];
    visible: boolean;
    isOpen: boolean;
    onClick: Function;
    alwaysExpanded?: boolean;
}

const ExtraCoverHelpBox = posed.div({
    exit: {
        height: 0,
        opacity: OpacityValues.NO_OPACITY,
        transition: {
            ease: 'easeIn',
            duration: 0
        }
    },
    enter: {
        opacity: OpacityValues.FULL_OPACITY,
        height: 'auto',
        transition: {
            ease: 'easeIn',
            duration: 300
        }
    }
});

const CoverageListElement: React.FunctionComponent<CoverageProps> = ({
    coverageData,
    availableCoverages,
    visible,
    onClick,
    isOpen,
    alwaysExpanded
}) => {
    const { scroll, ScrollTo } = useScroll();
    const smoothScroll = useNextRenderCall(scroll);
    const handleCoverHelpClick = (isActive: boolean) => {
        if (isActive) {
            onClick();
            if (!isOpen) {
                smoothScroll();
            }
        }
    };

    const enabled: boolean = Object.values(availableCoverages).includes(
        coverageData.coverageName
    );

    const Container = posed.li({
        open: {
            delay: 600,
            opacity: OpacityValues.FULL_OPACITY
        },
        closed: {
            opacity: alwaysExpanded
                ? OpacityValues.FULL_OPACITY
                : OpacityValues.NO_OPACITY,
            duration: 900
        }
    });

    return (
        <ScrollTo>
            <Container
                className={classNames({
                    'hidden-element': !coverageData.coverageName
                })}
                pose={visible ? 'open' : 'closed'}
                key={coverageData.coverageName}>
                <a
                    onClick={() => handleCoverHelpClick(enabled)}
                    className={
                        'no-underline green-on-hover display-inline-flex align-items'
                    }>
                    {isOkq8() ? (
                        <Check
                            className={classNames(
                                'dont-hide-card-click margin-right-13 margin-left-39',
                                { 'hidden-element': !enabled }
                            )}
                            color={'#20419A'}
                        />
                    ) : (
                        <Text
                            size={'h6'}
                            className={classNames(
                                'margin-right-8',
                                'dont-hide-card-click',
                                enabled
                                    ? 'available-cover-disc'
                                    : 'disabled-cover-disc'
                            )}>
                            {isOpen ? <>&#9650;</> : <>&#9660;</>}
                        </Text>
                    )}
                    <Text
                        size={'h5'}
                        className={classNames(
                            'green-on-hover',
                            'display-inline-flex',
                            'dont-hide-card-click',
                            {
                                'disabled-cover': !enabled
                            },
                            {
                                'okq8-grey-label': isOkq8() && enabled
                            },
                            {
                                'okq8-light-blue-color': isOkq8() && !enabled
                            }
                        )}>
                        <HtmlPanel
                            className={classNames('dont-hide-card-click')}
                            onClick={() => handleCoverHelpClick(enabled)}>
                            {coverageData.coverageName}
                        </HtmlPanel>
                    </Text>
                </a>
                <div className="extra-coverages-list-element">
                    <PoseGroup flipMove={false}>
                        {isOpen &&
                            coverageData.coverageDescription && [
                                <ExtraCoverHelpBox
                                    className={classNames(
                                        { 'margin-left-55': isOkq8() },
                                        'coverage-arrow-box dont-hide-card-click'
                                    )}
                                    key="extraCoverHelpBox">
                                    <Text
                                        className={classNames(
                                            'dont-hide-card-click',
                                            { 'okq8-grey-label': isOkq8() }
                                        )}>
                                        <HtmlPanel
                                            className={'dont-hide-card-click'}>
                                            {coverageData.coverageDescription}
                                        </HtmlPanel>
                                    </Text>
                                </ExtraCoverHelpBox>
                            ]}
                    </PoseGroup>
                </div>
            </Container>
        </ScrollTo>
    );
};

export default CoverageListElement;
